import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  Hammer,
  Lightbulb,
  Handshake,
  Compass,
  Eye,
  Quote,
  ExternalLink,
  Newspaper,
  Calendar,
} from 'lucide-react';
import { SectionHeader, FadeUp } from '../components/SectionHeader';
import { IMAGES, VALUES, BRAND } from '../data/content';
import {
  CHAIRMAN,
  EXECUTIVE_SECRETARY,
  ORG_DIVISIONS,
  CORPORATE_FUNCTIONS,
  PRESS_NEWS,
} from '../data/about';

const VALUE_ICONS = [Hammer, Shield, Lightbulb, Compass, Handshake, Eye];

const TIMELINE = [
  { year: '2010', title: 'Founded', desc: 'Clan Group established in Mumbai with industrial engineering focus.' },
  { year: '2014', title: 'Textile Engineering', desc: 'Clan Tex Installations launched — turnkey textile plant delivery.' },
  { year: '2017', title: 'EPC Expansion', desc: 'Process piping, fire hydrant systems and steel structures scaled.' },
  { year: '2020', title: 'International', desc: 'First international turnkey projects in Africa and Middle East.' },
  { year: '2022', title: 'Welding Automation', desc: 'Weldmatix Fusion launched — global welding automation systems.' },
  { year: '2024', title: 'Technology & SaaS', desc: 'Zolvix Technologies launched — Solve · Build · Scale.' },
];

const TYPE_COLORS = {
  Launch: '#D4AF37',
  Milestone: '#1B3A8A',
  Project: '#2A9D8F',
  Expansion: '#0D1B2A',
};

function formatNewsDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch {
    return iso;
  }
}

export default function About() {
  return (
    <div className="bg-white">
      <section className="relative pt-44 pb-24 bg-[#0D1B2A] grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${IMAGES.team})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(40%)',
          }}
        />
        <div className="absolute inset-0 bg-[#0D1B2A]/82" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="overline text-white/80 flex items-center gap-3 mb-6">
            <span className="gold-rule" /> About Clan Group
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            A diversified industrial group <span className="text-[#D4AF37]">engineered for the long term.</span>
          </h1>
          <p className="mt-8 text-white/75 text-lg max-w-3xl leading-relaxed">
            Headquartered in {BRAND.hq} and operating across {BRAND.regions.join(', ')}, Clan Group brings
            engineering depth, project execution capability and innovative technologies under a single
            integrated platform.
          </p>
        </div>
      </section>

      {/* Chairman's Message */}
      <section className="bg-[#F5F7FA] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <FadeUp className="lg:col-span-5">
              <div className="relative">
                <img
                  src={CHAIRMAN.photo}
                  alt={CHAIRMAN.name}
                  className="w-full h-[480px] lg:h-[600px] object-cover grayscale-[20%]"
                />
                <div className="absolute -bottom-5 -left-5 bg-[#D4AF37] text-[#0D1B2A] px-6 py-5 max-w-[280px]">
                  <div className="text-xs uppercase tracking-[0.22em] font-bold">Chairman</div>
                  <div className="font-display text-lg font-bold mt-1">{CHAIRMAN.name}</div>
                </div>
              </div>
            </FadeUp>

            <div className="lg:col-span-7 lg:pt-6">
              <div className="overline mb-5 flex items-center gap-3">
                <span className="gold-rule" /> Chairman&apos;s Message
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0D1B2A] tracking-tight leading-[1.08]">
                &ldquo;We engineer progress — one disciplined project at a time.&rdquo;
              </h2>
              <div className="mt-8 relative pl-9">
                <Quote
                  size={26}
                  className="absolute left-0 top-0 text-[#D4AF37]"
                  strokeWidth={1.5}
                />
                <div className="space-y-5 text-[#0D1B2A] text-[16px] leading-relaxed">
                  {CHAIRMAN.message.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
              <div className="mt-10 pt-6 border-t border-[#0D1B2A]/10">
                <div className="font-display font-bold text-[#0D1B2A] text-xl">
                  {CHAIRMAN.name}
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mt-1">
                  {CHAIRMAN.designation} · Clan Group
                </div>
                <p className="mt-5 text-[#4B5563] text-[14px] leading-relaxed max-w-2xl">
                  {CHAIRMAN.bio}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-10 md:gap-16">
          <FadeUp>
            <div className="overline mb-5 flex items-center gap-3">
              <span className="gold-rule" /> Vision
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0D1B2A] tracking-tight leading-tight">
              To build a globally respected group of businesses delivering engineering excellence,
              technological innovation and sustainable value across industries.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="overline mb-5 flex items-center gap-3">
              <span className="gold-rule" /> Mission
            </div>
            <p className="text-[#0D1B2A] text-lg md:text-xl leading-relaxed">
              To execute every project with precision, integrity and accountability — while
              continuously expanding our capabilities, empowering our people and creating long-term
              value for clients, partners and communities.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Org Chart */}
      <section className="bg-[#0D1B2A] py-24 md:py-32 grain relative">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader
            overline="Group Structure"
            title="A platform built around the Chairman&apos;s office."
            lead="Six operating divisions and four group functions — coordinated through the Chairman&apos;s office for strategic alignment and execution discipline."
            light
          />

          <div className="mt-16 relative">
            {/* Chairman block */}
            <div className="grid lg:grid-cols-12 gap-6 items-start">
              <div className="lg:col-span-12 flex justify-center">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="bg-white text-[#0D1B2A] px-8 py-6 min-w-[300px] text-center shadow-2xl border-t-4 border-[#D4AF37]"
                >
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                    Chairman
                  </div>
                  <div className="font-display font-bold text-2xl mt-2">Chairman&apos;s Office</div>
                  <div className="text-xs text-[#4B5563] mt-1">Group Strategy · Governance</div>
                </motion.div>
              </div>
            </div>

            {/* Vertical line + Exec Secretary on the side */}
            <div className="relative mt-6 grid lg:grid-cols-12">
              <div className="hidden lg:block lg:col-span-12">
                <div className="mx-auto w-px h-12 bg-[#D4AF37]/60" />
              </div>
            </div>

            {/* Exec Secretary branch */}
            <div className="relative grid lg:grid-cols-12 gap-6 -mt-2">
              <div className="lg:col-span-7" />
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="lg:col-span-5 relative"
              >
                <div className="hidden lg:block absolute left-0 top-7 w-12 h-px bg-[#D4AF37]/60" />
                <div className="bg-[#132338] border border-white/10 text-white px-6 py-5 lg:ml-12">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                    Chairman&apos;s Office
                  </div>
                  <div className="font-display font-bold text-lg mt-1">
                    {EXECUTIVE_SECRETARY.designation}
                  </div>
                  <div className="text-xs text-white/55 mt-1">
                    Liaison · Scheduling · Board Coordination
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Divisions row */}
            <div className="mt-12 relative">
              <div className="hidden lg:block absolute left-1/2 -top-12 w-px h-12 bg-[#D4AF37]/60" />
              <div className="text-center mb-10">
                <div className="overline text-white/60 inline-flex items-center gap-3">
                  <span className="gold-rule" /> Operating Divisions <span className="gold-rule" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {ORG_DIVISIONS.map((d, i) => (
                  <motion.div
                    key={d.slug}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.05 }}
                    className="bg-white p-6 relative overflow-hidden"
                  >
                    <span
                      className="absolute top-0 left-0 h-1 w-full"
                      style={{ background: d.accent }}
                    />
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0D1B2A]/55">
                      Division {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="font-display font-bold text-[#0D1B2A] text-lg mt-1.5 flex items-center gap-2">
                      {d.name}
                      {d.external && <ExternalLink size={13} className="text-[#D4AF37]" />}
                    </div>
                    <div className="text-xs text-[#4B5563] mt-1.5">{d.head}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Corporate functions */}
            <div className="mt-14">
              <div className="text-center mb-8">
                <div className="overline text-white/60 inline-flex items-center gap-3">
                  <span className="gold-rule" /> Corporate Functions <span className="gold-rule" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {CORPORATE_FUNCTIONS.map((f, i) => (
                  <motion.div
                    key={f.name}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="bg-[#132338] border border-white/8 p-5"
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                      Function
                    </div>
                    <div className="font-display font-semibold text-white mt-1.5">{f.name}</div>
                    <div className="text-xs text-white/55 mt-1">{f.head}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader
            overline="Core Values"
            title="Six principles. One way of working."
            lead="The standards that define how we engineer, deliver and partner — across every site, every project, every geography."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-[#F5F7FA] p-8 border border-[#0D1B2A]/8 card-hover-lift"
                >
                  <Icon size={26} className="text-[#D4AF37]" />
                  <h3 className="mt-5 font-display font-bold text-xl text-[#0D1B2A]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader overline="Our Story" title="Milestones across 15+ years." />
          <div className="mt-14 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[#0D1B2A]/10" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className={`relative pl-12 md:pl-0 md:grid md:grid-cols-2 md:gap-12 ${
                    i % 2 === 0 ? '' : 'md:[&>div:first-child]:order-2'
                  }`}
                >
                  <div className={`${i % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'}`}>
                    <div className="font-stat font-extrabold text-3xl text-[#D4AF37]">{t.year}</div>
                    <h3 className="font-display font-bold text-xl text-[#0D1B2A] mt-1">{t.title}</h3>
                    <p className="text-[14px] text-[#4B5563] mt-2 leading-relaxed">{t.desc}</p>
                  </div>
                  <div className="hidden md:block" />
                  <div className="absolute left-4 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#D4AF37] ring-4 ring-[#F5F7FA]" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Press & News */}
      <section id="press" className="bg-white py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <SectionHeader
              overline="Press & News"
              title="Updates from across the group."
              lead="A selection of recent launches, milestones and project highlights from Clan Group and our portfolio companies."
            />
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[#0D1B2A] font-semibold whitespace-nowrap hover:text-[#D4AF37] transition-colors"
            >
              Media enquiries <ArrowRight size={16} />
            </Link>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRESS_NEWS.map((n, i) => (
              <motion.article
                key={n.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group bg-white border border-[#0D1B2A]/10 card-hover-lift p-7 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 text-white"
                    style={{ background: TYPE_COLORS[n.type] || '#0D1B2A' }}
                  >
                    {n.type}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] inline-flex items-center gap-1.5">
                    <Calendar size={11} /> {formatNewsDate(n.date)}
                  </span>
                </div>
                <h3 className="mt-5 font-display font-bold text-lg text-[#0D1B2A] leading-snug">
                  {n.title}
                </h3>
                <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed flex-1">{n.summary}</p>
                {n.href ? (
                  <a
                    href={n.href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 pt-4 border-t border-[#0D1B2A]/8 inline-flex items-center gap-1.5 text-sm font-bold text-[#0D1B2A] hover:text-[#D4AF37] transition-colors"
                  >
                    Read more <ExternalLink size={13} />
                  </a>
                ) : (
                  <div className="mt-5 pt-4 border-t border-[#0D1B2A]/8 inline-flex items-center gap-1.5 text-xs text-[#4B5563]">
                    <Newspaper size={12} /> Press release
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0D1B2A] py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="overline mb-3 flex items-center gap-3">
              <span className="gold-rule" /> Work With Us
            </div>
            <h3 className="font-display text-white text-2xl md:text-3xl font-bold">
              Want to partner, supply or build a career with us?
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">
              Contact Us <ArrowRight size={16} />
            </Link>
            <Link to="/careers" className="btn-secondary">
              Explore Careers <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
